'use client'

import React, { useState, useCallback } from 'react'
import type { NumberedListBlock as NumberedListBlockProps } from '@/payload-types'
import { ToggleButton } from '@/components/ui/toggle-button'
import { HorizontalScrollContainer } from '@/components/ui/horizontal-scroll'
import { Media } from '@/components/Media'
import { cn } from '@/utilities/ui'
import { getBlockPadding } from '@/utilities/getBlockPadding'
import { getMediaUrl } from '@/utilities/getMediaUrl'

// Media styling constants
const backgroundColors: Record<string, string> = {
  beige: 'bg-civo-cloud',
  black: 'bg-civo-black',
  white: 'bg-white',
}

type Props = NumberedListBlockProps & {
  disableInnerContainer?: boolean
}

interface NumberedItemProps {
  number: string
  title: string
  description: string
  isExpanded: boolean
  onHover: () => void
  isAccordion: boolean
  isFirst: boolean
  showNumber: boolean
}

interface GridItemProps {
  number: string
  title: string
  description: string
  showNumber: boolean
}

interface ProcessedItem {
  id?: string | null
  title?: string | null
  description?: string | null
  number: string
}

function GridItem({ number, title, description, showNumber }: GridItemProps) {
  return (
    <article className="group flex flex-col gap-11 min-w-[240px] flex-1 pt-6 border-t border-civo-cloud">
      {/* Number with train board animation */}
      {showNumber && (
        <div className="relative overflow-hidden h-9 lg:h-[52px]">
          <p className="display-small text-black/70 transition-transform duration-300 ease-in-out group-hover:-translate-y-full">
            {number}
          </p>
          <p
            className="absolute left-0 top-0 display-small text-black/70 translate-y-full transition-transform duration-300 ease-in-out group-hover:translate-y-0"
            aria-hidden="true"
          >
            {number}
          </p>
        </div>
      )}

      {/* Title and Description */}
      <div className="flex flex-col gap-6">
        <h3 className="subheading-medium min-h-14 text-black">
          {title}
        </h3>
        <p className="body-copy-medium text-black/70">
          {description}
        </p>
      </div>
    </article>
  )
}

interface WithImageItemProps {
  number: string
  title: string
  description: string
  showNumber: boolean
  isFirst: boolean
}

function WithImageItem({ number, title, description, showNumber, isFirst }: WithImageItemProps) {
  return (
    <div className={cn('group flex gap-3 py-5', !isFirst && 'border-t border-civo-cloud')}>
      {/* Number column */}
      {showNumber && (
        <span className="w-8 shrink-0 body-copy-medium font-medium text-civo-cloud transition-colors duration-300 group-hover:text-black">
          {number}
        </span>
      )}
      {/* Content */}
      <div className="flex flex-col gap-2 flex-1">
        <h3 className="body-large text-black">{title}</h3>
        <p className="body-copy-medium text-black/70">{description}</p>
      </div>
    </div>
  )
}

function NumberedItem({
  number,
  title,
  description,
  isExpanded,
  onHover,
  isAccordion,
  isFirst,
  showNumber,
}: NumberedItemProps) {
  if (!isAccordion) {
    // Simple variation - always show description
    return (
      <div className="flex flex-col gap-4 border-b border-civo-cloud py-4 first:border-t first:lg:border-t-0 first:lg:pt-0">
        {showNumber && (
          <p className="body-copy-medium font-medium text-black/70">{number}</p>
        )}
        <h3 className="subheading-small lg:subheading-large text-black">{title}</h3>
        <p className="body-copy-medium lg:body-large text-black/70">{description}</p>
      </div>
    )
  }

  // Accordion variation - simple on mobile/tablet, accordion on desktop
  return (
    <div
      className="flex flex-col w-full border-b border-civo-cloud first:border-t first:lg:border-t-0"
      onMouseEnter={onHover}
    >
      {/* Mobile/Tablet: Simple layout - always visible */}
      <div className="lg:hidden py-6">
        <div className={cn('flex', showNumber && 'gap-4')}>
          {showNumber && (
            <span className="body-small font-medium text-black/70 w-[34px] shrink-0">{number}</span>
          )}
          <div className="flex flex-col gap-2">
            <h3 className="subheading-small text-black">{title}</h3>
            <p className="body-copy-medium text-black/70">{description}</p>
          </div>
        </div>
      </div>

      {/* Desktop: Accordion layout */}
      <div className="hidden lg:block">
        <div
          className={cn(
            'flex items-center justify-between w-full gap-2 xl:gap-6 py-6',
            isFirst && 'pt-0',
          )}
        >
          <div className="flex items-center flex-1 min-w-0">
            {showNumber && (
              <span className="body-copy-medium font-medium text-black/70 w-[50px] shrink-0">
                {number}
              </span>
            )}
            <h3 className="subheading-large text-black flex-1">{title}</h3>
          </div>
          <ToggleButton isExpanded={isExpanded} selectedColor="black" unselectedColor="lime" />
        </div>

        {/* Content with smooth height animation */}
        <div
          className={cn(
            'grid transition-[grid-template-rows,margin] duration-300 ease-in-out',
            isExpanded ? '-mt-4' : 'mt-0',
          )}
          style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
        >
          <div className="overflow-hidden">
            <div
              className={cn(
                'pb-6 transition-opacity duration-300 ease-in-out',
                isExpanded ? 'opacity-100' : 'opacity-0',
              )}
            >
              <div className="pr-[74px]">
                <p className="body-copy-medium text-black/70">{description}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}


export const NumberedListBlock: React.FC<Props> = ({
  variation = 'accordion',
  title,
  description,
  mediaType = 'backgroundImage',
  backgroundImage,
  media,
  mediaBackground = 'beige',
  mediaPosition = 'bottom-right',
  columnPosition = 'right',
  showNumbers = true,
  items,
  disableInnerContainer,
  paddingTop,
  paddingBottom,
}) => {
  // Get padding classes based on layout options
  const paddingClasses = getBlockPadding({
    paddingTop: paddingTop ?? true,
    paddingBottom: paddingBottom ?? true,
  })
  const isAccordion = variation === 'accordion'
  const isGrid = variation === 'grid'
  const itemsArray = Array.isArray(items) ? items : []
  const showNumber = showNumbers !== false

  const [expandedIndex, setExpandedIndex] = useState<number | null>(
    isAccordion && itemsArray.length > 0 ? 0 : null,
  )

  const handleItemInteraction = useCallback((index: number) => {
    setExpandedIndex(index)
  }, [])

  if (itemsArray.length === 0) {
    return null
  }

  if (isGrid && itemsArray.length < 2) {
    return null
  }

  const processedItems: ProcessedItem[] = itemsArray.map((item, index) => ({
    ...item,
    number: String(index + 1).padStart(2, '0'),
  }))

  // Grid variation
  if (isGrid) {
    return (
      <div
        className={cn(
          'box-border flex flex-col items-start justify-start relative size-full overflow-hidden',
          paddingClasses,
          { container: !disableInnerContainer },
        )}
      >
        <div className="w-full overflow-hidden">
          {/* XL Desktop: All items in a row, no scroll */}
          <div className="hidden xl:flex gap-6 w-full">
            {processedItems.map((item, index) => (
              <GridItem
                key={item.id || `grid-item-xl-${index}`}
                number={item.number}
                title={item.title || ''}
                description={item.description || ''}
                showNumber={showNumber}
              />
            ))}
          </div>

          {/* Below XL: Scrollable with responsive item widths */}
          <div className="xl:hidden overflow-hidden">
            <HorizontalScrollContainer gap="gap-6" scrollbarClassName="mt-16">
              {processedItems.map((item, index) => (
                <div
                  key={item.id || `grid-item-${index}`}
                  className="flex-shrink-0 w-[calc(85%-11px)] md:w-[calc(42%-12px)]"
                >
                  <GridItem
                    number={item.number}
                    title={item.title || ''}
                    description={item.description || ''}
                    showNumber={showNumber}
                  />
                </div>
              ))}
            </HorizontalScrollContainer>
          </div>
        </div>
      </div>
    )
  }

  // With Image variation
  if (variation === 'withImage') {
    const imageOnLeft = columnPosition === 'left'
    const backgroundImageResource = typeof backgroundImage === 'object' ? backgroundImage : undefined
    const mediaResource = typeof media === 'object' ? media : undefined

    // Flexbox alignment classes for image positioning (non-absolute)
    const flexPositionClasses: Record<string, string> = {
      'top-left': 'justify-start items-start',
      'top-right': 'justify-end items-start',
      'bottom-left': 'justify-start items-end',
      'bottom-right': 'justify-end items-end',
    }

    // Render media column content based on mediaType
    const renderMediaContent = (isMobile = false) => {
      const baseClasses = isMobile
        ? 'w-full aspect-square md:aspect-auto md:h-[508px] rounded-[16px] overflow-hidden'
        : 'w-1/2 rounded-[16px] overflow-hidden'

      if (mediaType === 'backgroundImage' && backgroundImageResource) {
        const backgroundImageUrl = getMediaUrl(backgroundImageResource.url, backgroundImageResource.updatedAt)
        return (
          <div
            className={cn(baseClasses, !isMobile && 'aspect-square')}
            style={{
              backgroundImage: `url(${backgroundImageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
            role="img"
            aria-label={backgroundImageResource.alt || ''}
          />
        )
      }

      if (mediaType === 'image' && mediaResource) {
        return (
          <div
            className={cn(
              'flex',
              baseClasses,
              backgroundColors[mediaBackground || 'beige'] || backgroundColors.beige,
              flexPositionClasses[mediaPosition || 'bottom-right'] || flexPositionClasses['bottom-right'],
            )}
          >
            <Media
              resource={mediaResource}
              imgClassName="w-full h-auto object-contain"
              disablePlaceholder
            />
          </div>
        )
      }

      return null
    }

    // Shared items list for mobile and desktop
    const itemsList = (
      <div className="flex flex-col">
        {processedItems.map((item, index) => (
          <WithImageItem
            key={item.id || `with-image-item-${index}`}
            number={item.number}
            title={item.title || ''}
            description={item.description || ''}
            showNumber={showNumber}
            isFirst={index === 0}
          />
        ))}
      </div>
    )

    return (
      <div
        className={cn(
          'box-border flex flex-col items-start justify-start relative size-full',
          paddingClasses,
          { container: !disableInnerContainer },
        )}
      >
        {/* Mobile/Tablet: Stacked layout */}
        <div className="lg:hidden flex flex-col gap-8 w-full">
          {renderMediaContent(true)}
          <div className="flex flex-col gap-4">
            {title && <h2 className="display-small text-black whitespace-pre-line">{title}</h2>}
            {description && <p className="body-large text-black/70">{description}</p>}
            {itemsList}
          </div>
        </div>

        {/* Desktop: Two-column with configurable order */}
        <div
          className={cn('hidden lg:flex items-start gap-8 w-full', imageOnLeft && 'flex-row-reverse')}
        >
          {/* Text column */}
          <div className="flex flex-col gap-6 w-1/2">
            {title && <h2 className="display-large text-black whitespace-pre-line">{title}</h2>}
            {description && <p className="body-large text-black/70">{description}</p>}
            {itemsList}
          </div>
          {/* Media column */}
          {renderMediaContent(false)}
        </div>
      </div>
    )
  }

  // Accordion/Simple variation
  return (
    <div
      className={cn(
        'box-border flex flex-col gap-11 lg:gap-[72px] items-start justify-start relative size-full',
        paddingClasses,
        { container: !disableInnerContainer },
      )}
    >
      <div className="flex w-full flex-col lg:flex-row gap-8 lg:gap-8">
        <div className="flex flex-col gap-6 w-full lg:w-1/2 min-w-0">
          {title && <h2 className="display-small lg:display-large text-black whitespace-pre-line">{title}</h2>}
          {description && (
            <p className="body-copy-medium lg:body-large font-medium text-black/70">{description}</p>
          )}
        </div>

        <div className="flex flex-col w-full lg:w-1/2">
          {processedItems.map((item, index) => (
            <NumberedItem
              key={item.id || `item-${index}`}
              number={item.number}
              title={item.title || ''}
              description={item.description || ''}
              isExpanded={expandedIndex === index}
              onHover={() => handleItemInteraction(index)}
              isAccordion={isAccordion}
              isFirst={index === 0}
              showNumber={showNumbers !== false}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
